<template>
  <container>
    <div class="cr-page">
      <div class="pg-title-row">
        <div>
          <h1 class="pg-title">New Race</h1>
          <p class="pg-title-sub">Create a scheduled LiveRun session with stream, media and participant settings</p>
        </div>
        <router-link to="/admin/races" id="Cancel" class="cr-ghost-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 12H5"/>
            <path d="M12 19l-7-7 7-7"/>
          </svg>
          Races
        </router-link>
      </div>

      <custom-form :handle-submit="handleSubmit" class="cr-form">
        <section class="cr-card">
          <div class="cr-card-header">
            <span class="cr-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 6h13"/>
                <path d="M8 12h13"/>
                <path d="M8 18h13"/>
                <path d="M3 6h.01"/>
                <path d="M3 12h.01"/>
                <path d="M3 18h.01"/>
              </svg>
            </span>
            <div>
              <h2 class="cr-card-title">Race Details</h2>
              <p class="cr-card-sub">Name, description, timing and base capacity for the session</p>
            </div>
          </div>

          <div class="cr-grid cr-grid--two">
            <text-field class="cr-field" v-model="race.name" label="Name" name="name" v-validate="'required'" maxlength="5" data-vv-value-path="input" data-vv-name="name" error-message="Fill in the name" :has-error="errors.has('name')" :required="true"></text-field>

            <date-time-field class="cr-field" v-model="race.date" :label="`Date - ${getTimezone()}`" name="date" v-validate="'required'" data-vv-value-path="startDate.time" data-vv-name="date" error-message="Fill in the date" :has-error="errors.has('date')" :required="true"></date-time-field>

            <div class="form__group cr-field cr-field--full">
              <label for="description">Description <span class="required">*</span></label>
              <textarea v-validate data-vv-rules="required" :class="{'form-control': true, 'is-danger': errors.has('description') }" name="description" rows="6" v-model="race.description" id="txtArea" @keydown.enter.prevent="onDescriptionChange" required></textarea>
              <span v-show="errors.has('description')" class="help is-danger">Fill in the description</span>
            </div>

            <div class="form__group cr-field">
              <label for="duration">Duration in minutes <span class="required">*</span></label>
              <input v-validate data-vv-rules="required" :class="{'form-control': true, 'is-danger': errors.has('duration') }" type="number" step="1" min="1" :max="max_duration" name="duration" v-model="race.duration" required>
              <span v-show="errors.has('duration')" class="help is-danger">Fill in the duration</span>
            </div>

            <div class="form__group cr-field">
              <label for="rsvp_limit">RSVP Limit <span class="required">*</span></label>
              <input v-validate data-vv-rules="required" :class="{'form-control': true, 'is-danger': errors.has('rsvp_limit') }" type="number" step="1" min="1" :max="max_rsvp_limit" name="rsvp_limit" v-model="race.rsvp_limit" required>
              <span v-show="errors.has('rsvp_limit')" class="help is-danger">Fill in the RSVP limit</span>
            </div>
          </div>
        </section>

        <section class="cr-card">
          <div class="cr-card-header cr-card-header--split">
            <div class="cr-card-heading">
              <span class="cr-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 1l4 4-4 4"/>
                  <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <path d="M7 23l-4-4 4-4"/>
                  <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
              </span>
              <div>
                <h2 class="cr-card-title">Repeat Schedule</h2>
                <p class="cr-card-sub">Optionally generate recurring race sessions from this setup</p>
              </div>
            </div>
            <div class="cr-toggle-wrap">
              <span class="mode-env-badge">{{ isRepeatShift ? 'Repeating' : 'One time' }}</span>
              <lv-toggle v-model="isRepeatShift" @change="onChecked" @click.native.prevent></lv-toggle>
            </div>
          </div>

          <div v-if="isRepeatShift" class="cr-repeat-grid">
            <div class="form__group cr-field">
              <label for="repeatDays">Repeat Every <span class="required">*</span></label>
              <select class="cr-select" v-model="race.interval" name="repeatDays" v-validate="isRepeatShift?'required':''" :required="isRepeatShift">
                <option value="EVERY_WEEK">Every Week</option>
                <option value="EVERY_2_WEEKS">Every 2 Weeks</option>
                <option value="EVERY_MONTH">Every Month</option>
              </select>
              <span v-show="errors.has('repeatDays')" class="help is-danger">Fill in the Repeat Days</span>
            </div>
            <date-time-field class="cr-field endingDate" v-model="race.end_interval" :isRepeatField="true" name="endDate" v-validate="isRepeatShift?'required':''" data-vv-value-path="startDate.time" data-vv-name="endDate" error-message="Fill in the End date" :has-error="errors.has('endDate')" :data-vv-rules="isRepeatShift?'required':''" :required="isRepeatShift"></date-time-field>
          </div>
        </section>

        <section class="cr-card">
          <div class="cr-card-header">
            <span class="cr-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16v16H4z"/>
                <path d="M4 9h16"/>
                <path d="M9 20V9"/>
              </svg>
            </span>
            <div>
              <h2 class="cr-card-title">Type And Stream</h2>
              <p class="cr-card-sub">Choose medal behavior, category labels, language and audio channel</p>
            </div>
          </div>

          <div class="cr-grid cr-grid--two">
            <div class="form__group cr-field">
              <label for="type">First Type</label>
              <select class="cr-select" v-model="race.type" label="First Type" name="type">
                <option value="LIVE">Live (Medal)</option>
                <option value="NO_MEDAL">No Medal</option>
                <option value="ON_DEMAND_MEDAL">On demand (Medal)</option>
                <option value="ON_DEMAND_NO_MEDAL">On demand (No Medal)</option>
              </select>
            </div>

            <race-media v-if="race.type == 'ON_DEMAND_MEDAL' || race.type == 'ON_DEMAND_NO_MEDAL'" class="cr-field" v-model="race.audioFileUrl" label="On demand File" name="media" data-vv-value-path="input" data-vv-name="media" error-message="Fill in the audio" :has-error="errors.has('media')" :required="false"></race-media>

            <div class="form__group cr-field">
              <label for="secondType">Second Type</label>
              <div v-if="raceTypesLoading" class="cr-sk-input sk-shimmer"></div>
              <select v-else class="cr-select" v-model="race.secondType" label="Second Type" name="secondType">
                <option v-for="type in raceTypes" :key="type.name" v-bind:value="type.name">{{ type.englishText }}</option>
              </select>
            </div>

            <div class="form__group cr-field">
              <label for="thirdType">Third Type</label>
              <select class="cr-select" v-model="race.thirdType" label="Third Type" name="thirdType">
                <option value="">NONE</option>
                <option value="NO_MEDAL">NO MEDAL</option>
                <option value="PromoL">L</option>
                <option value="PromoI">I</option>
                <option value="PromoV">V</option>
                <option value="PromoE">E</option>
                <option value="LIVE_60MIN">60MIN_MEDAL</option>
                <option value="PromoU">U</option>
                <option value="PromoR">R</option>
                <option value="MEDAL_20K">20Km Medal</option>
              </select>
            </div>

            <div class="form__group cr-field">
              <label for="channel">Channel</label>
              <div v-if="channelsLoading" class="cr-sk-input sk-shimmer"></div>
              <select v-else class="cr-select" v-model="race.mobileChannelToPlay" label="Channel" name="channel">
                <option :value="channels.audioChannelA">A</option>
                <option :value="channels.audioChannelB">B</option>
                <option :value="channels.audioChannelC">C</option>
                <option :value="channels.audioChannelD">D</option>
              </select>
            </div>

            <div class="form__group cr-field">
              <label for="language">Language</label>
              <select class="cr-select" v-model="race.language" label="Language" name="language">
                <option v-for="locale in locales" :key="locale.id" v-bind:value="locale.id">{{ locale.name }}</option>
              </select>
            </div>
          </div>
        </section>

        <section class="cr-card">
          <div class="cr-card-header">
            <span class="cr-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11.5 4.43"/>
                <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07l1.33-1.33"/>
              </svg>
            </span>
            <div>
              <h2 class="cr-card-title">Action Link</h2>
              <p class="cr-card-sub">Configure the end-session URL and optional action image</p>
            </div>
          </div>

          <div class="cr-grid cr-grid--two">
            <div class="cr-field">
              <label class="cr-label" for="endSessionUrl">Action URL</label>
              <text-field v-model="race.endSessionUrl" name="endSessionUrl" data-vv-value-path="input" data-vv-name="name"></text-field>
            </div>
            <race-photo class="cr-field" v-model="race.actionImageUrl" label="Action Image" name="actionImage" data-vv-value-path="input" data-vv-name="actionImage" error-message="Fill in the photo with size below 200 Kb" :has-error="errors.has('actionImage')" :required="false"></race-photo>
          </div>
        </section>

        <section class="cr-card">
          <div class="cr-card-header cr-card-header--split">
            <div class="cr-card-heading">
              <span class="cr-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                  <path d="M7 7h.01"/>
                </svg>
              </span>
              <div>
                <h2 class="cr-card-title">Tags And Trainer</h2>
                <p class="cr-card-sub">Assign audience tags, club randomization and the trainer owner</p>
              </div>
            </div>
            <div class="cr-toggle-wrap">
              <span class="mode-env-badge">{{ race.randomClubTag ? 'Club tag on' : 'Club tag off' }}</span>
              <lv-toggle v-model="race.randomClubTag" @click.native.prevent></lv-toggle>
            </div>
          </div>

          <div class="cr-grid cr-grid--two">
            <div class="form__group cr-field race-tags">
              <label for="tags">Tags</label>
              <div v-if="tagsLoading" class="cr-sk-input sk-shimmer"></div>
              <selectize v-else v-bind:options="tagsOptions" v-model="selectedTags" multiple size="1" :isRunnerTag="false" data-dropdown-parent="body" data-max-options="100" data-value-field="id" data-label-field="name" data-search-field="name" data-sort-field="name" class="cr-selectize-hidden"></selectize>
            </div>

            <div class="form__group cr-field">
              <label for="Trainer">Trainer</label>
              <div v-if="trainerLoading" class="cr-sk-input sk-shimmer"></div>
              <select v-else v-model="trainerId" label="Trainer" name="Trainer" @change="changeTrainer" class="cr-select trainer-list">
                <option v-for="(trainer, index) in trainerList" v-bind:value="trainer.id" :key="index">{{ trainer.firstName }}</option>
              </select>
            </div>
          </div>
        </section>

        <section class="cr-card">
          <div class="cr-card-header">
            <span class="cr-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </span>
            <div>
              <h2 class="cr-card-title">Race Photo</h2>
              <p class="cr-card-sub">Upload the required image shown with this race in the mobile app</p>
            </div>
          </div>

          <div class="cr-photo-grid">
            <div v-if="race.imageUrl" class="cr-photo-preview">
              <span class="cr-photo-label">Current race photo</span>
              <img :src="race.imageUrl" class="cr-photo-img" alt="Current race photo">
            </div>
            <upload-photo class="cr-field" v-model="race.imageUrl" label="Photo" name="photo" v-validate="'required'" data-vv-value-path="input" data-vv-name="photo" error-message="Fill in the photo with size below 200 Kb" :has-error="errors.has('photo')" :required="true"></upload-photo>
          </div>
        </section>

        <div class="cr-actions">
          <router-link to="/admin/races" class="cr-ghost-btn">
            Cancel
          </router-link>
          <button class="cr-primary-btn" id="submit-form" :disabled="isSubmitting">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
            {{ isSubmitting ? 'Creating...' : 'Create Race' }}
          </button>
        </div>
      </custom-form>
    </div>
  </container>
</template>

<script>
  import {
    mapGetters,
    mapActions
  } from 'vuex'
  import Container from '@/components/Container'
  import CustomForm from '@/components/Forms/Form'
  import TextField from '@/components/Forms/TextField'
  import DateTimeField from '@/components/Forms/DateTimeField'
  import RacePhoto from '@/components/Forms/RacePhoto'
  import UploadPhoto from '@/components/Forms/UploadPhoto'
  import RaceMedia from '@/components/Forms/RaceMedia'
  import LvToggle from '@/components/Forms/LvToggle'
  import SettingsService from "@/services/settings"
  import TrainerService from '@/services/trainer'
  import Selectize from '@/components/Selectize'
  import TagService from '../../services/tag'
  import RaceService from '../../services/races'
  import { db } from '../../firebase'
  export default {
    name: 'CreateRace',
    data: () => ({
      race: {
        name: '',
        description: '',
        notes: '',
        date: '',
        startDate: '',
        finishDate: '',
        type: 'LIVE',
        secondType: 'RUN_OR_WALK',
        thirdType: '', //https://app.asana.com/0/1137425856170315/1200180518958154
        //thirdType:'NO_MEDAL',
        mobileChannelToPlay: '',
        language: 'ja',
        imageUrl: '',
        audioFileUrl: '',
        duration: null,
        rsvp_limit: 1000,
        randomClubTag: false,
        endSessionUrl: ""
        //end_interval:'',
        //interval:''
      },
      locales: [ {id: 'en', name: 'English'}, {id: 'ja', name: 'Japanese'}],
      types: [],
      channels: {},
      tagsOptions: [],
      selectedTags: [],
      isRepeatShift: false,
      repeatEvery: '',
      endDate: '',
      trainerList: [],
      trainerId: '',
      raceTrainer: '',
      raceTypes: [],
      isSubmitting: false,
      channelsLoading: true,
      tagsLoading: true,
      trainerLoading: true,
      raceTypesLoading: true,
      max_duration: 1440, // 24 hours in minutes
      max_rsvp_limit: 10000 // Maximum RSVP participants
    }),
    computed: {
      ...mapGetters({
        trainer: 'Trainer/trainer'
      })
    },
    methods: {
      handleSubmit() {
        //console.log("this.raceinn---",this.race)

        if (this.race.thirdType == '') {
          this.race.thirdType = null
        }

        var tagList = []
        this.selectedTags.forEach(tag => {
          tagList.push({'id': parseInt(tag)})
        })
        this.race.tags = tagList

        this.$validator.validateAll()
          .then(success => {
            if (!success) {
              return
            }

            //this.race.trainer = this.trainer
            this.race.trainer = this.raceTrainer // CHANGE THE TRAINER OF SESSION

            this.isSubmitting = true

            this.createRace(this.race)
              .then((resp) => {
                console.log("respo---", resp.data)
                // if(resp.data.id){
                //   this.selectedTags.forEach(tag => {
                //      TagService.addRaceTag(resp.data.id,tag).then((response)=>{
                //         console.log("addRaceTag---",tag)
                //     })
                //   })
                // }
                var respData = resp.data
                var that = this
                console.log("resp.data.raceIdsInterval---", respData.raceIdsInterval)
                var RaceId = respData.id
                this.addValueInFirebase(respData, RaceId)
                // db.ref(`/${this.$fbDbEntry}/${respData.id}`).update({
                //   'streamingUrl': respData.mobileChannelToPlay,
                //   'endSessionUrl':respData.endSessionUrl //Add Action url in Firebase
                // }).catch(error => {
                //   console.log("Firebase error--",error)
                // })
                if (respData.raceIdsInterval != undefined) {
                  respData.raceIdsInterval.forEach(function (raceId) {
                    console.log("---", raceId)
                    that.addValueInFirebase(respData, raceId)
                    // db.ref(`/${that.$fbDbEntry}/${raceId}`).update({
                    //   'streamingUrl': respData.mobileChannelToPlay,
                    //   'endSessionUrl':respData.endSessionUrl //Add Action url in Firebase for repeating races
                    // }).catch(error => {
                    //   console.log("Firebase error--",error)
                    // })
                  })
                }

                this.$router.push({
                  path: '/admin/races'
                })
                this.error = ''
                this.isSubmitting = false
              })
              .catch(() => {
                this.error = 'Failed!'
                this.isSubmitting = false
              })
          })
      },
      addValueInFirebase(respData, raceId) {
        db.ref(`/${this.$fbDbEntry}/${raceId}`).update({
          'streamingUrl': respData.mobileChannelToPlay,
          'endSessionUrl': respData.endSessionUrl //Add Action url in Firebase
        }).catch(error => {
          console.log("Firebase error--", error)
        })
      },
      onChecked() {},
      ...mapActions({
        createRace: 'Race/createOne',
        getRaceTypes: 'Race/getTypes'
      }),
      getTimezone() {
        return new Date().toString().match(/([A-Z]+[+-][0-9]+.*)/)[1]
      },
      onDescriptionChange() {
        this.race.description = `${this.race.description}\n*`
      },
      async getExistingChannels() {
        this.channelsLoading = true
        try {
          const channels = await SettingsService.get()
          this.channels = channels.data
          this.race.mobileChannelToPlay = channels.data.audioChannelA
          return channels.data
        } finally {
          this.channelsLoading = false
        }
      },
      getAllTags() {
        this.tagsLoading = true
        //Session Creation : Randomly tag "club".
        var randomNum = Math.round(Math.random())
        var that = this
        TagService.getAllTags().then((response) => {
          console.log("response----", response)
          this.tagsOptions = response.data
          //Session Creation : Randomly tag "club".
          if (randomNum == 1) {
            setTimeout(function () { that.selectedTags = ["35"] }, 100)
          }
        }).finally(() => {
          this.tagsLoading = false
        })
      },
      getTrainerList() {
        this.trainerLoading = true
        var that = this
        setTimeout(function () {
          //SET CURRENT LOGIN TRAINER BY DEFAULT
          that.trainerId = that.trainer.id
          that.raceTrainer = that.trainer
        }, 1000)
        //GET TRAINER LIST
        TrainerService.getAll().then(response => {
          if (response.data && response.data != undefined && response.data != '') {
            this.trainerList = response.data
          }
        }).catch(error => {
          console.log("Error get all trainer--->>", error)
        }).finally(() => {
          this.trainerLoading = false
        })
      },
      changeTrainer() {
        // CHANGE THE TRAINER OF SESSION
        TrainerService.getTrainer(this.trainerId).then(response => {
          this.raceTrainer = response.data
        }).catch(error => {
          console.log("Error Get trainer by id--->>", error)
        })
      },
      getRaceTypeAvailable() {
        this.raceTypesLoading = true
        RaceService.getRaceTypeAvailable().then(response => {
          console.log("response>>>", response.data)
          if (response.data && response.data != undefined && response.data != '') {
            let allRaceTypes = response.data
            //Remove these from the second type (https://app.asana.com/0/1137425856170315/1200251895400268)
            let removeItem = ["SOLO", "PromoL", "PromoI", "PromoV", "PromoE", "PromoU", "PromoR", "NO_MEDAL", "LIVE_60MIN", "ON_DEMAND_MEDAL", "ON_DEMAND_NO_MEDAL", "WALK"]
            _.remove(allRaceTypes, obj => removeItem.includes(obj.name))
            // var addElements = allRaceTypes.push({englishText: 'PromoU', name: 'PromoU', japaneseText: 'PromoU'}, {englishText: 'PromoR', name: 'PromoR', japaneseText: 'PromoR'})
            this.raceTypes = allRaceTypes
            // this.raceTypes = allRaceTypes.push( {englishText: 'PromoU', name: 'PromoU', japaneseText: 'PromoU'}, {englishText: 'PromoR', name: 'PromoR', japaneseText: 'PromoR'})
            //this.raceTypes = response.data
          }
        }).catch(error => {
          console.log("Error getting race type--->>", error)
        }).finally(() => {
          this.raceTypesLoading = false
        })
      }
    },
    created() {
      this.getAllTags()
      this.getExistingChannels()
      this.getTrainerList()
      this.getRaceTypeAvailable()
      this.getRaceTypes().then(res => {
        res.data.unshift('NONE')
        this.types = res.data
      })
    },
    components: {
      Container,
      CustomForm,
      TextField,
      DateTimeField,
      RacePhoto,
      UploadPhoto,
      RaceMedia,
      LvToggle,
      Selectize
    }
  }
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.cr-page {
  padding-bottom: 2rem;
}

.cr-form.form-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 40px;
}

.cr-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.cr-card-header,
.cr-card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cr-card-header {
  margin-bottom: 1.25rem;
}

.cr-card-header--split {
  justify-content: space-between;
  gap: 1rem;
}

.cr-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
}

.cr-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.2;
}

.cr-card-sub {
  font-size: 12px;
  color: #8a8a9a;
  margin: 4px 0 0;
  line-height: 1.35;
}

.cr-grid {
  display: grid;
  gap: 1rem 1.25rem;
}

.cr-grid--two,
.cr-repeat-grid,
.cr-photo-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.cr-repeat-grid,
.cr-photo-grid {
  display: grid;
  gap: 1rem 1.25rem;
}

.cr-field--full {
  grid-column: 1 / -1;
}

.cr-page .form__group,
.cr-field {
  margin-bottom: 0;
  min-width: 0;
}

.cr-page .form__group > label,
.cr-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 7px;
}

.cr-page .required {
  color: #c0392b;
}

.cr-page .form-control,
.cr-select,
.cr-page textarea.form-control {
  width: 100%;
  min-height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  line-height: 1.35;
  padding: 0 12px;
  box-shadow: none;
}

.cr-page .datepickbox,
.cr-page .cov-vue-date {
  width: 100%;
}

.cr-page .cov-datepicker {
  width: 100% !important;
  min-height: 36px !important;
  border: 1px solid #e8e8ed !important;
  border-radius: 8px !important;
  background: #fafafa !important;
  color: #1a1a2e !important;
  font-size: 13px !important;
  line-height: 1.35 !important;
  padding: 0 12px !important;
  box-shadow: none !important;
}

.cr-page textarea.form-control {
  min-height: 118px;
  padding: 10px 12px;
  resize: vertical;
}

.cr-page .form-control:focus,
.cr-select:focus,
.cr-page textarea.form-control:focus {
  outline: none;
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.cr-page .is-danger {
  border-color: #c0392b;
}

.cr-page .help.is-danger {
  display: block;
  font-size: 12px;
  color: #c0392b;
  margin-top: 6px;
}

.cr-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='%238a8a9a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 11px center;
  padding-right: 34px;
}

.cr-toggle-wrap {
  height: 28px;
  display: inline-grid;
  grid-template-columns: auto 44px;
  align-items: center;
  column-gap: 10px;
  flex: 0 0 auto;
  margin-left: auto;
}

.cr-toggle-wrap .lv-toggle {
  display: block;
  align-self: center;
  margin: 0;
}

.mode-env-badge {
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  align-self: center;
  box-sizing: border-box;
}

.cr-sk-input {
  width: 100%;
  height: 36px;
}

.cr-page .race-tags .selectize-control {
  width: 100%;
}

.cr-page .race-tags .selectize-control.multi .selectize-input {
  min-height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  box-shadow: none;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  box-sizing: border-box;
}

.cr-page .race-tags .selectize-control.multi .selectize-input.focus {
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.cr-page .race-tags .selectize-control.multi .selectize-input > div.active {
  background: #e5e2ff;
  color: #413b94;
  border: 0 solid transparent;
}

.cr-page .race-tags .selectize-control.multi .selectize-input > div {
  max-width: 100%;
  min-height: 26px;
  border-radius: 999px;
  background: #f3f1ff;
  color: #413b94;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 4px 6px 4px 10px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cr-page .race-tags .selectize-control.plugin-remove_button .selectize-input > div .remove {
  width: 18px;
  height: 18px;
  border-left: 0;
  border-radius: 50%;
  color: #6b5fd6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  margin: 0 0 0 2px;
  padding: 0;
  position: static;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  opacity: 0.85;
}

.cr-page .race-tags .selectize-control.plugin-remove_button .selectize-input > div .remove:hover {
  background: #e5e2ff;
  color: #4338ca;
  opacity: 1;
}

.cr-page .race-tags .selectize-input > input {
  font-size: 13px;
  color: #1a1a2e;
  min-width: 120px;
}

.cr-photo-preview {
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fafafa;
  padding: 12px;
}

.cr-photo-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #8a8a9a;
  margin-bottom: 8px;
}

.cr-photo-img {
  width: 100%;
  max-height: 180px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.cr-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding-top: 0.5rem;
}

.cr-primary-btn,
.cr-ghost-btn {
  height: 36px;
  min-height: 36px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.18s ease;
  vertical-align: middle;
}

.cr-form.form-container .cr-actions .cr-primary-btn,
.cr-form.form-container .cr-actions .cr-ghost-btn {
  margin: 0;
}

.cr-primary-btn {
  appearance: none;
  background: #5b4fcf;
  color: #fff;
  border: none;
  padding: 0 18px;
}

.cr-primary-btn:hover {
  background: #4338ca;
}

.cr-primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.cr-ghost-btn {
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  min-width: 82px;
  padding: 0 14px;
}

.cr-ghost-btn:hover {
  background: #f0effe;
}

@media (max-width: 760px) {
  .pg-title-row,
  .cr-card-header--split {
    align-items: flex-start;
    flex-direction: column;
  }

  .cr-grid--two,
  .cr-repeat-grid,
  .cr-photo-grid {
    grid-template-columns: 1fr;
  }

  .cr-card {
    padding: 1.25rem;
  }

  .cr-toggle-wrap {
    margin-left: 0;
    align-self: flex-end;
    justify-self: end;
  }

  .cr-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .cr-primary-btn,
  .cr-ghost-btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// Create.vue?ce1f9d1a